import { FiltroPlanta, TablaBatchesDetalle } from '@/components/dominio';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useBatchesDetalle } from '@/hooks/useBatchesDetalle';
import { ChevronLeft, ChevronRight, Search, X } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * `/analisis-batches` — vista global de batches con datos crudos de planta.
 *
 * Pensada para auditar la calidad de producción: el laboratorio (o calidad)
 * filtra por planta/fecha/colada y revisa columnas configurables (agua,
 * humedad, aditivos, deltas) para detectar batches con datos alterados.
 *
 * Filtros persistidos en URL para compartir/recargar.
 */

const POR_PAGINA = 100;

export function AnalisisBatchesPantalla() {
  const [searchParams, setSearchParams] = useSearchParams();

  const setParams = useCallback(
    (changes: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(changes)) {
            if (v === null || v === '') next.delete(k);
            else next.set(k, v);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const idPlantaRaw = searchParams.get('id_planta');
  const idPlanta = idPlantaRaw !== null && /^\d+$/.test(idPlantaRaw) ? Number(idPlantaRaw) : null;
  // Llega desde "Datos de planta" en el detalle de una muestra: acota la
  // vista a los batches de esa colada puntual (no un filtro editable más).
  const idColadaRaw = searchParams.get('id_colada');
  const idColada = idColadaRaw !== null && /^\d+$/.test(idColadaRaw) ? Number(idColadaRaw) : null;
  const desde = searchParams.get('desde') ?? '';
  const hasta = searchParams.get('hasta') ?? '';
  const q = searchParams.get('q') ?? '';
  const soloAnomalias = searchParams.get('solo_anomalias') === 'true';
  const paginaRaw = searchParams.get('pagina');
  const pagina = paginaRaw !== null && /^\d+$/.test(paginaRaw) ? Math.max(1, Number(paginaRaw)) : 1;

  const filtros = useMemo(
    () => ({
      id_colada: idColada ?? undefined,
      id_planta: idPlanta ?? undefined,
      desde: desde || undefined,
      hasta: hasta || undefined,
      q: q.trim() || undefined,
      solo_anomalias: soloAnomalias || undefined,
      pagina,
      por_pagina: POR_PAGINA,
    }),
    [idColada, idPlanta, desde, hasta, q, soloAnomalias, pagina],
  );

  const consulta = useBatchesDetalle(filtros);
  const data = consulta.data?.data;
  const batches = data?.batches ?? [];
  const total = data?.total ?? 0;
  const totalPaginas = Math.max(1, Math.ceil(total / POR_PAGINA));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Análisis de batches</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Datos crudos de planta por batch (agua, humedad, aditivos, desviaciones). Útil para
          investigar por qué una colada no rindió la resistencia esperada.
        </p>
      </div>

      {idColada !== null && (
        <div className="flex items-center justify-between rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
          <span>
            Filtrando por{' '}
            <span className="font-medium">
              colada #{batches[0]?.codigo_interno_colada ?? idColada}
            </span>
            . Los filtros de abajo se combinan con este.
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1 text-emerald-900 hover:bg-emerald-100"
            onClick={() => setParams({ id_colada: null, pagina: null })}
          >
            <X className="h-3.5 w-3.5" /> Quitar
          </Button>
        </div>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Filtros</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-4">
          <div className="grid gap-1.5">
            <Label>Planta</Label>
            <FiltroPlanta
              idPlanta={idPlanta}
              onChange={(v) =>
                setParams({ id_planta: v === null ? null : String(v), pagina: null })
              }
            />
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="b-desde">Desde</Label>
            <Input
              id="b-desde"
              type="date"
              value={desde}
              onChange={(e) => setParams({ desde: e.target.value || null, pagina: null })}
              className="w-[160px]"
            />
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="b-hasta">Hasta</Label>
            <Input
              id="b-hasta"
              type="date"
              value={hasta}
              onChange={(e) => setParams({ hasta: e.target.value || null, pagina: null })}
              className="w-[160px]"
            />
          </div>

          <div className="grid flex-1 min-w-[200px] gap-1.5">
            <Label htmlFor="b-q">Buscar</Label>
            <div className="relative">
              <Search
                className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                id="b-q"
                value={q}
                onChange={(e) => setParams({ q: e.target.value || null, pagina: null })}
                placeholder="Receta, destino, N° record…"
                className="pl-8"
              />
            </div>
          </div>

          <label className="flex cursor-pointer items-center gap-2 pb-2 text-sm">
            <input
              type="checkbox"
              checked={soloAnomalias}
              onChange={(e) =>
                setParams({ solo_anomalias: e.target.checked ? 'true' : null, pagina: null })
              }
              className="h-4 w-4"
            />
            Solo con alarma / receta modificada
          </label>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-3">
          <TablaBatchesDetalle batches={batches} cargando={consulta.isLoading} />

          {!consulta.isLoading && total > 0 && (
            <div className="mt-3 flex items-center justify-between border-t pt-3">
              <div className="text-sm text-muted-foreground">
                Mostrando {(pagina - 1) * POR_PAGINA + 1}–
                {(pagina - 1) * POR_PAGINA + batches.length} de {total}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagina <= 1}
                  onClick={() => setParams({ pagina: pagina - 1 <= 1 ? null : String(pagina - 1) })}
                >
                  <ChevronLeft className="h-4 w-4" /> Anterior
                </Button>
                <span className="text-sm tabular-nums">
                  {pagina} / {totalPaginas}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagina >= totalPaginas}
                  onClick={() => setParams({ pagina: String(pagina + 1) })}
                >
                  Siguiente <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {consulta.error && (
        <p className="text-sm text-destructive">
          Error al cargar batches: {consulta.error.message}
        </p>
      )}
    </div>
  );
}
